import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Mail, Send, FlaskConical, RefreshCw, ToggleRight } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from './ui/alert-dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin/migrations`;
const opts = { withCredentials: true };
const CHUNK = 20;

const EMAILS = [
  { key: 'gratitude', path: 'send-founder-gratitude', title: "Founder's Gratitude", step: 'Day 1' },
  { key: 'theme', path: 'send-theme-launch', title: 'Theme Launch', step: 'Day 2+' },
];

const btn = 'inline-flex items-center gap-1.5 px-3 py-1.5 font-mono text-xs border rounded-none disabled:opacity-40 disabled:cursor-not-allowed transition-colors';

function EmailRow({ cfg, stats, busy, blocked, onTest, onBatch }) {
  const done = stats.remaining === 0 && stats.sent > 0;
  return (
    <div className="border border-[#2A0F3D] p-4" data-testid={`launch-email-row-${cfg.key}`}>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="font-mono text-[10px] text-[#6B8E73] tracking-widest">{cfg.step}</div>
          <div className="font-mono text-sm text-white">{cfg.title}</div>
        </div>
        <div className="font-mono text-xs text-[#A3C7A8]" data-testid={`launch-email-counts-${cfg.key}`}>
          <span className="text-[#39FF14]">{stats.sent}</span> sent · <span className="text-[#FFD700]">{stats.remaining}</span> left
          {stats.skipped > 0 && <> · <span className="text-[#FF9F0A]">{stats.skipped}</span> skipped</>}
          {done && <span className="ml-2 text-[#39FF14]">✓ DONE</span>}
        </div>
      </div>
      {stats.skipped > 0 && (
        <details className="mt-2 font-mono text-[11px] text-[#6B8E73]" data-testid={`launch-email-skipped-${cfg.key}`}>
          <summary className="cursor-pointer hover:text-[#FF9F0A]">{stats.skipped} skipped: bounced / on the suppression list (never emailed)</summary>
          <ul className="mt-1 pl-3 space-y-0.5">
            {stats.skipped_emails.map((e) => <li key={e} className="break-all">{e}</li>)}
          </ul>
        </details>
      )}
      {blocked && <p className="mt-2 font-mono text-[11px] text-[#FFD700]" data-testid={`launch-email-blocked-${cfg.key}`}>{blocked}</p>}
      <div className="mt-3 flex gap-2 flex-wrap">
        <button className={`${btn} border-[#2A0F3D] text-[#A3C7A8] hover:border-[#39FF14]`} disabled={busy} onClick={onTest} data-testid={`launch-email-test-${cfg.key}`}>
          <FlaskConical className="w-3.5 h-3.5" /> Send test to me
        </button>
        <button className={`${btn} border-[#39FF14] text-[#39FF14] hover:bg-[#39FF14]/10`} disabled={busy || !!blocked || stats.remaining === 0} onClick={onBatch} data-testid={`launch-email-batch-${cfg.key}`}>
          <Send className="w-3.5 h-3.5" /> {busy ? 'Sending…' : `Send today's batch (${Math.min(stats.remaining, 80)})`}
        </button>
      </div>
    </div>
  );
}

export function LaunchEmailsPanel() {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(null);
  const [confirm, setConfirm] = useState(null);

  const load = useCallback(async () => {
    try { setStatus((await axios.get(`${API}/launch-emails/status`, opts)).data); }
    catch (e) { toast.error(`Launch emails: ${e?.response?.data?.detail || e.message}`); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!status) return null;
  const allDone = ['gratitude', 'theme'].every((k) => status[k].remaining === 0 && status[k].sent > 0);
  if (allDone) return null;

  const flagsOn = status.flags.modern_theme_toggle && status.flags.theme_feedback_poll;
  const blockedFor = (k) => {
    if (k !== 'theme') return null;
    if (status.gratitude.remaining > 0) return "Finish Founder's Gratitude first.";
    if (!flagsOn) return 'Turn on the theme flags first.';
    return null;
  };

  const sendTest = async (cfg) => {
    setBusy(cfg.key);
    try {
      const { data } = await axios.post(`${API}/${cfg.path}`, { commit: true, only_email: user.email }, opts);
      data.sent_count ? toast.success(`Test sent to ${user.email}`) : toast.error(data.note || 'Test not sent');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Test failed'); }
    finally { setBusy(null); }
  };

  const sendBatch = async (cfg) => {
    setBusy(cfg.key);
    let sent = 0, failed = 0;
    try {
      for (let i = 0; i < status.daily_cap / CHUNK; i++) {
        const { data } = await axios.post(`${API}/${cfg.path}`, { commit: true, limit: CHUNK }, opts);
        sent += data.sent_count || 0;
        failed += data.failed_count || 0;
        if (!data.plan_count || !data.remaining) break;
      }
      toast.success(`${cfg.title}: ${sent} sent${failed ? `, ${failed} failed` : ''}`);
    } catch (e) { toast.error(e?.response?.data?.detail || `Stopped after ${sent} sent`); }
    finally { setBusy(null); load(); }
  };

  const turnOnFlags = async () => {
    try {
      await Promise.all(['customer.modern_theme_toggle', 'customer.theme_feedback_poll'].map((k) =>
        axios.patch(`${process.env.REACT_APP_BACKEND_URL}/api/feature-flags/${k}`, { enabled: true }, opts)));
      toast.success('Theme flags ON');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Flag update failed'); }
  };

  return (
    <section className="mb-8 border border-[#2A0F3D] bg-[#0C0612] p-5" data-testid="launch-emails-panel">
      <div className="flex items-center justify-between mb-1">
        <h2 className="font-mono text-[#39FF14] text-sm font-bold flex items-center gap-2"><Mail className="w-4 h-4" /> LAUNCH_EMAILS</h2>
        <button onClick={load} className="text-[#6B8E73] hover:text-[#39FF14]" data-testid="launch-emails-refresh"><RefreshCw className="w-4 h-4" /></button>
      </div>
      <p className="font-mono text-[11px] text-[#6B8E73] mb-4">// Max {status.daily_cap}/day (Resend free = 100/day). Run once a day until 0 left. This card hides itself when both are done.</p>
      {status.cutoff && (
        <p className="font-mono text-[11px] text-[#A3C7A8] mb-4" data-testid="launch-emails-cutoff">
          // Only customers who joined before {new Date(status.cutoff).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'America/New_York' })}. Newer sign-ups get the welcome email with tiers instead.
        </p>
      )}
      <div className="space-y-3">
        {EMAILS.map((cfg) => (
          <EmailRow key={cfg.key} cfg={cfg} stats={status[cfg.key]} busy={busy === cfg.key}
            blocked={blockedFor(cfg.key)} onTest={() => sendTest(cfg)} onBatch={() => setConfirm(cfg)} />
        ))}
      </div>
      {!flagsOn && status.gratitude.remaining === 0 && (
        <button onClick={turnOnFlags} className={`${btn} mt-3 border-[#FFD700] text-[#FFD700] hover:bg-[#FFD700]/10`} data-testid="launch-emails-flags-on">
          <ToggleRight className="w-3.5 h-3.5" /> Turn on Modern theme + feedback poll
        </button>
      )}
      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent data-testid="launch-emails-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Send {confirm?.title}?</AlertDialogTitle>
            <AlertDialogDescription>
              This emails up to {confirm && Math.min(status[confirm.key].remaining, status.daily_cap)} customers right now. People who already got it are skipped.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="launch-emails-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="launch-emails-confirm-send" onClick={() => { const c = confirm; setConfirm(null); sendBatch(c); }}>Send</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
